import { Box, Flex, Icon, List, ListIcon, ListItem, Text, VStack } from '@chakra-ui/react';
import { FaBullhorn, FaUserPlus, FaGift, FaCheckCircle } from 'react-icons/fa';
import { headingFont } from 'theme/tokens';
import { translate } from 'i18n';
import WhatsAppCta from '../components/WhatsAppCta';

const STEPS = [
  {
    icon: FaBullhorn,
    titleKey: 'marketing.referral_step1_title',
    descKey: 'marketing.referral_step1_desc',
    color: 'brand.500',
    bg: 'brand.50',
  },
  {
    icon: FaUserPlus,
    titleKey: 'marketing.referral_step2_title',
    descKey: 'marketing.referral_step2_desc',
    color: 'accent.500',
    bg: 'green.50',
  },
  {
    icon: FaGift,
    titleKey: 'marketing.referral_step3_title',
    descKey: 'marketing.referral_step3_desc',
    color: 'purple.500',
    bg: 'purple.50',
  },
] as const;

const BENEFITS = [
  'marketing.referral_benefit1',
  'marketing.referral_benefit2',
  'marketing.referral_benefit3',
] as const;

export default function ReferralPage() {
  return (
    <Box>
      {/* Hero */}
      <Box
        bg="linear-gradient(135deg, #f8fafc 0%, #f0fdf4 50%, #eef2ff 100%)"
        px={{ base: 4, md: 8 }}
        pt={{ base: 12, md: 20 }}
        pb={{ base: 8, md: 14 }}
      >
        <VStack maxW="700px" mx="auto" spacing={5} textAlign="center">
          <Text
            fontFamily={headingFont}
            fontSize={{ base: '3xl', md: '5xl' }}
            fontWeight="800"
            lineHeight="1.1"
            letterSpacing="-0.03em"
            color="gray.900"
          >
            {translate('marketing.referral_title')}
          </Text>
          <Text fontSize={{ base: 'md', md: 'lg' }} color="gray.600" maxW="540px">
            {translate('marketing.referral_subtitle')}
          </Text>
        </VStack>
      </Box>

      {/* 3-step process */}
      <Box px={{ base: 4, md: 8 }} py={{ base: 8, md: 14 }}>
        <VStack maxW="800px" mx="auto" spacing={10}>
          <Text
            fontFamily={headingFont}
            fontWeight="700"
            fontSize={{ base: 'lg', md: 'xl' }}
            color="gray.800"
          >
            {translate('marketing.referral_how_title')}
          </Text>

          <Flex
            direction={{ base: 'column', md: 'row' }}
            gap={8}
            w="100%"
          >
            {STEPS.map((step, i) => (
              <VStack
                key={i}
                flex={1}
                bg="white"
                border="1px solid"
                borderColor="gray.200"
                borderRadius="2xl"
                p={8}
                spacing={4}
                align="center"
                textAlign="center"
                position="relative"
              >
                {/* Step number */}
                <Text
                  position="absolute"
                  top={3}
                  right={4}
                  fontSize="xs"
                  fontWeight="800"
                  color="gray.300"
                >
                  {i + 1}/3
                </Text>

                <Flex
                  w="56px"
                  h="56px"
                  borderRadius="full"
                  bg={step.bg}
                  align="center"
                  justify="center"
                >
                  <Icon as={step.icon} color={step.color} boxSize={6} />
                </Flex>
                <Text fontFamily={headingFont} fontWeight="700" fontSize="md" color="gray.800">
                  {translate(step.titleKey as any)}
                </Text>
                <Text fontSize="sm" color="gray.600" lineHeight="1.7">
                  {translate(step.descKey as any)}
                </Text>
              </VStack>
            ))}
          </Flex>
        </VStack>
      </Box>

      {/* Benefits + CTA */}
      <Box bg="gray.50" px={{ base: 4, md: 8 }} py={14}>
        <VStack maxW="500px" mx="auto" spacing={8} textAlign="center">
          <List spacing={3} textAlign="left">
            {BENEFITS.map((key) => (
              <ListItem
                key={key}
                display="flex"
                alignItems="center"
                fontSize="md"
                color="gray.700"
              >
                <ListIcon as={FaCheckCircle} color="accent.500" boxSize={5} />
                {translate(key as any)}
              </ListItem>
            ))}
          </List>

          <WhatsAppCta>
            {translate('marketing.referral_cta')}
          </WhatsAppCta>
        </VStack>
      </Box>
    </Box>
  );
}
